import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { SidebarProvider } from './contexts/SidebarContext';
import ErrorBoundary from './components/ErrorBoundary';
import { NotificationProvider } from './components/NotificationSystem';
import websocketService from './services/websocketService';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ForceChangePasswordPage from './pages/ForceChangePasswordPage';
import PasswordRecoveryRequestsPage from './pages/PasswordRecoveryRequestsPage';
import DashboardPage from './pages/DashboardPage';
import MissingAgentsSharePage from './pages/MissingAgentsSharePage';
import ClockInOutPage from './pages/ClockInOutPage';
import SchedulePage from './pages/SchedulePage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import UsersManagementPage from './pages/UsersManagementPage';
import DepartmentsManagementPage from './pages/DepartmentsManagementPage';
import CampaignsManagementPage from './pages/CampaignsManagementPage';
import RolesManagementPage from './pages/RolesManagementPage';
import TimeRecordsManagementPage from './pages/TimeRecordsManagementPage';
import TimeRecordsReportPage from './pages/TimeRecordsReportPage';
import DailySummaryReportPage from './pages/DailySummaryReportPage';
import PayrollReviewPage from './pages/PayrollReviewPage';
import BonusTrackerPage from './pages/BonusTrackerPage';
import BonusCategoriesPage from './pages/BonusCategoriesPage';
import CampaignPerformanceReportPage from './pages/CampaignPerformanceReportPage';
import EmployeeAttendanceReportPage from './pages/EmployeeAttendanceReportPage';
import EmployeeTimeSheetReportPage from './pages/EmployeeTimeSheetReportPage';
import ManualTimeAdjustmentsReportPage from './pages/ManualTimeAdjustmentsReportPage';
import WeeklyAttendanceReportPage from './pages/WeeklyAttendanceReportPage';
import WeeklyStaffingCoverageReportPage from './pages/WeeklyStaffingCoverageReportPage';
import TimeOffRequestsReportPage from './pages/TimeOffRequestsReportPage';
import AbsenceReportPage from './pages/AbsenceReportPage';
import TardiesReportPage from './pages/TardiesReportPage';
import TimeEntryValidationPage from './pages/TimeEntryValidationPage';
import ScheduledEmployeesReportPage from './pages/ScheduledEmployeesReportPage';
import TimeAbsenceRequestsPage from './pages/TimeAbsenceRequestsPage';
import LiveEmployeeStatusPage from './pages/LiveEmployeeStatusPage';
import ScheduleManagementPage from './pages/ScheduleManagementPage';
import DailyAttendanceCoveragePage from './pages/DailyAttendanceCoveragePage';
import NotificationSettingsPage from './pages/NotificationSettingsPage';
import MySchedulePage from './pages/MySchedulePage';
import ProfilePage from './pages/ProfilePage';
import NetworkConfigPage from './pages/NetworkConfigPage';
import DeviceRestrictionsPage from './pages/DeviceRestrictionsPage';
import AlertSystemPage from './pages/AlertSystemPage';
import ActivityLogPage from './pages/ActivityLogPage';
import NotificationPreferencesPage from './pages/NotificationPreferencesPage';
import AgentHomePage from './pages/AgentHomePage';
import MissingPunchesReportPage from './pages/MissingPunchesReportPage';
import IdleSummaryReportPage from './pages/IdleSummaryReportPage';
import DeviceUptimeReportPage from './pages/DeviceUptimeReportPage';
import WorkforceMonitoringLayout from './components/workforceMonitoring/WorkforceMonitoringLayout';
import OverviewPage from './pages/workforceMonitoring/OverviewPage';
import WorkforcePage from './pages/workforceMonitoring/WorkforcePage';
import ComputersPage from './pages/workforceMonitoring/ComputersPage';
import ComputerDetailPage from './pages/workforceMonitoring/ComputerDetailPage';
import ActivityPage from './pages/workforceMonitoring/ActivityPage';
import ApplicationsPage from './pages/workforceMonitoring/ApplicationsPage';
import WebActivityPage from './pages/workforceMonitoring/WebActivityPage';
import SessionsPage from './pages/workforceMonitoring/SessionsPage';
import AlertsPage from './pages/workforceMonitoring/AlertsPage';
import MonitoringReportsPage from './pages/workforceMonitoring/ReportsPage';
import ConfigurationPage from './pages/workforceMonitoring/ConfigurationPage';
import EnforcementPoliciesPage from './pages/workforceMonitoring/EnforcementPoliciesPage';
import LiveWallPage from './pages/workforceMonitoring/LiveWallPage';
import IpApprovalPage from './pages/IpApprovalPage';
import CallQaLayout from './components/callQa/CallQaLayout';
import CallQaOverviewPage from './pages/callQa/OverviewPage';
import CallQaCallsPage, { CallQaCallDetailPage } from './pages/callQa/CallsPage';
import CallQaConfigurationPage from './pages/callQa/ConfigurationPage';
import CallQaConductPage from './pages/callQa/ConductPage';
import CallQaScorecardsPage from './pages/callQa/ScorecardsPage';
import CallQaServicesPage from './pages/callQa/ServicesPage';
import CallQaAgentsPage from './pages/callQa/AgentsPage';
import CallQaCampaignsPage, { CallQaCampaignDetailPage } from './pages/callQa/CampaignsPage';
import CallQaInvestigationsPage, { CallQaInvestigationDetailPage } from './pages/callQa/InvestigationsPage';
import CallQaCoachingPage, { CallQaCoachingDetailPage } from './pages/callQa/CoachingPage';
import CallQaReportsPage from './pages/callQa/ReportsPage';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

const DeviceDiagnosticsLegacyRedirect: React.FC = () => {
  const { deviceId = '' } = useParams();
  return <Navigate to={`/workforce-monitoring/computers/${deviceId}?tab=diagnostics`} replace />;
};

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const AppRoutes: React.FC = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Initialize WebSocket when user is authenticated
  React.useEffect(() => {
    if (isAuthenticated && user?.id) {
      console.log('🔌 Initializing WebSocket for user:', user.id);
      websocketService.connect(user.id);
      websocketService.requestNotificationPermission();
      
      return () => {
        websocketService.disconnect();
      };
    }
  }, [isAuthenticated, user?.id]);

  // Show loading while checking authentication
  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={<Navigate to={`/login?returnUrl=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`} replace />} />
      </Routes>
    );
  }

  if (user?.mustChangePassword) {
    return (
      <Routes>
        <Route path="/force-change-password" element={<ForceChangePasswordPage />} />
        <Route path="*" element={<Navigate to="/force-change-password" replace />} />
      </Routes>
    );
  }

  return (
    <>
      <Layout>
      <Routes>
        <Route path="/" element={
          user?.role === 'agent' 
            ? <Navigate to="/home" replace />
            : <Navigate to="/dashboard" replace />
        } />
        <Route path="/operations/password-recovery" element={
          <ProtectedRoute
            requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user', 'hr']}
            requiredPermission="users.password_recovery.read"
          >
            <PasswordRecoveryRequestsPage />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          user?.role === 'agent' 
            ? <Navigate to="/home" replace />
            : <DashboardPage />
        } />
        <Route path="/dashboard/missing-agents/share/:token" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <MissingAgentsSharePage />
          </ProtectedRoute>
        } />
        <Route path="/home" element={
          user?.role === 'agent'
            ? <AgentHomePage />
            : <Navigate to="/dashboard" replace />
        } />
        <Route path="/clock" element={<ClockInOutPage />} />
        <Route path="/schedule" element={<SchedulePage />} />
        <Route path="/my-schedule" element={<MySchedulePage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/notifications" element={
          <ProtectedRoute requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <NotificationPreferencesPage />
          </ProtectedRoute>
        } />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/time-records" element={
          <ProtectedRoute requiredRole={['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <TimeRecordsReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/daily-summary" element={
          <ProtectedRoute requiredRole={['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <DailySummaryReportPage />
          </ProtectedRoute>
        } />
        {/* The former "Bonuses (TOGO)" screen is now the TEKS Solutions tab of the Bonus Tracker. Keep old links working. */}
        <Route path="/bonuses" element={<Navigate to="/bonus-tracker?tab=teks" replace />} />
        <Route path="/management/bonus-categories" element={
          <ProtectedRoute requiredRole={['super_user']}>
            <BonusCategoriesPage />
          </ProtectedRoute>
        } />
        {/* Bonus tracker: supervisors and above, plus the approvers named in Settings (the API enforces access). */}
        <Route path="/bonus-tracker" element={
          <ProtectedRoute>
            <BonusTrackerPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/payroll-review" element={
          <ProtectedRoute requiredRole={['hr', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <PayrollReviewPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/campaign-performance" element={
          <ProtectedRoute requiredRole={['manager', 'administrator', 'super_user']}>
            <CampaignPerformanceReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/employee-attendance" element={
          <ProtectedRoute requiredRole={['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <EmployeeAttendanceReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/employee-time-sheet" element={
          <ProtectedRoute requiredRole={['agent', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <EmployeeTimeSheetReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/manual-time-adjustments" element={
          <ProtectedRoute
            requiredRole={['hr', 'team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}
            requiredPermission="time_adjustments.read"
          >
            <ManualTimeAdjustmentsReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/weekly-attendance" element={
          <ProtectedRoute requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <WeeklyAttendanceReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/weekly-staffing-coverage" element={
          <ProtectedRoute requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <WeeklyStaffingCoverageReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/time-off-requests" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <TimeOffRequestsReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/absence" element={
          <ProtectedRoute requiredRole={['manager', 'administrator', 'super_user']}>
            <AbsenceReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/tardies" element={
          <ProtectedRoute requiredRole={['manager', 'administrator', 'super_user']}>
            <TardiesReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/time-entry-validation" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <TimeEntryValidationPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/scheduled-employees" element={
          <ProtectedRoute requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <ScheduledEmployeesReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/missing-punches" element={
          <ProtectedRoute
            requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}
            requiredPermission="time_entries.read"
          >
            <MissingPunchesReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/idle-summary" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <IdleSummaryReportPage />
          </ProtectedRoute>
        } />
        <Route path="/reports/device-uptime" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <DeviceUptimeReportPage />
          </ProtectedRoute>
        } />
        <Route path="/time-absence-requests" element={
          <ProtectedRoute>
            <TimeAbsenceRequestsPage />
          </ProtectedRoute>
        } />
        <Route path="/dashboard/live-status" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <LiveEmployeeStatusPage />
          </ProtectedRoute>
        } />
        <Route path="/management" element={
          <ProtectedRoute requiredRole={['manager', 'administrator', 'super_user']}>
            <UsersManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/management/users" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <UsersManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/management/departments" element={
          <ProtectedRoute requiredRole={['manager', 'administrator', 'super_user']}>
            <DepartmentsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/management/campaigns" element={
          <ProtectedRoute requiredRole={['manager', 'administrator', 'super_user']}>
            <CampaignsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/management/roles" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <RolesManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/management/time-records" element={
          <ProtectedRoute
            requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}
            requiredPermission="time_entries.read"
          >
            <TimeRecordsManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/management/schedules" element={
          <ProtectedRoute requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user']}>
            <ScheduleManagementPage />
          </ProtectedRoute>
        } />
        <Route path="/operations/attendance-coverage" element={
          <ProtectedRoute
            requiredRole={['team_lead', 'supervisor', 'manager', 'administrator', 'super_user', 'hr']}
            requiredPermission="operations.attendance_coverage.read"
          >
            <DailyAttendanceCoveragePage />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/settings/notifications" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <NotificationSettingsPage />
          </ProtectedRoute>
        } />
        <Route path="/system/network-config" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <NetworkConfigPage />
          </ProtectedRoute>
        } />
        <Route path="/system/device-restrictions" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <DeviceRestrictionsPage />
          </ProtectedRoute>
        } />
        <Route path="/system/alert-system" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <AlertSystemPage />
          </ProtectedRoute>
        } />
        <Route path="/system/activity-log" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <ActivityLogPage />
          </ProtectedRoute>
        } />
        <Route path="/system/device-inventory" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <Navigate to="/workforce-monitoring/computers" replace />
          </ProtectedRoute>
        } />
        <Route path="/system/tracking-monitoring" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <Navigate to="/workforce-monitoring/overview" replace />
          </ProtectedRoute>
        } />
        <Route path="/monitoring/computers" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <Navigate to="/workforce-monitoring/computers" replace />
          </ProtectedRoute>
        } />
        <Route path="/monitoring/computers/:deviceId/diagnostics" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <DeviceDiagnosticsLegacyRedirect />
          </ProtectedRoute>
        } />
        <Route path="/system/workforce-monitoring" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <Navigate to="/workforce-monitoring/overview" replace />
          </ProtectedRoute>
        } />
        <Route path="/workforce-monitoring" element={
          <ProtectedRoute
            requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}
            requiredPermission="monitoring.console.read"
          >
            <WorkforceMonitoringLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="workforce" element={<WorkforcePage />} />
          <Route path="computers" element={<ComputersPage />} />
          <Route path="computers/:deviceId" element={<ComputerDetailPage />} />
          <Route path="live-wall" element={<LiveWallPage />} />
          {/* Alias: user-facing name is Workforce View; keep live-wall for bookmarks/deep links */}
          <Route path="workforce-view" element={<Navigate to="/workforce-monitoring/live-wall" replace />} />
          <Route path="activity" element={<ActivityPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
          <Route path="web-activity" element={<WebActivityPage />} />
          <Route path="sessions" element={<SessionsPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="reports" element={<MonitoringReportsPage />} />
          <Route path="configuration" element={<ConfigurationPage />} />
          <Route path="configuration/enforcement-policies" element={<EnforcementPoliciesPage />} />
        </Route>
        <Route path="/call-qa" element={
          <ProtectedRoute requiredPermission={['call_qa.read']}>
            <CallQaLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<CallQaOverviewPage />} />
          <Route path="calls" element={<CallQaCallsPage />} />
          <Route path="calls/:callId" element={<CallQaCallDetailPage />} />
          <Route path="investigations" element={<CallQaInvestigationsPage />} />
          <Route path="investigations/:investigationId" element={<CallQaInvestigationDetailPage />} />
          <Route path="scorecards" element={<CallQaScorecardsPage />} />
          <Route path="services" element={<CallQaServicesPage />} />
          <Route path="agents" element={<CallQaAgentsPage />} />
          <Route path="conduct" element={<CallQaConductPage />} />
          <Route path="campaigns" element={<CallQaCampaignsPage />} />
          <Route path="campaigns/:campaignId" element={<CallQaCampaignDetailPage />} />
          <Route path="coaching" element={<CallQaCoachingPage />} />
          <Route path="coaching/:coachingId" element={<CallQaCoachingDetailPage />} />
          <Route path="reports" element={<CallQaReportsPage />} />
          <Route path="configuration" element={<CallQaConfigurationPage />} />
        </Route>
        <Route path="/workforce-monitoring/computers/:deviceId/diagnostics" element={
          <ProtectedRoute requiredRole={['supervisor', 'manager', 'administrator', 'super_user']}>
            <DeviceDiagnosticsLegacyRedirect />
          </ProtectedRoute>
        } />
        <Route path="/network/approve-ip" element={
          <ProtectedRoute requiredRole={['administrator', 'super_user']}>
            <IpApprovalPage />
          </ProtectedRoute>
        } />
        <Route path="/login" element={
          user?.role === 'agent'
            ? <Navigate to="/home" replace />
            : <Navigate to="/dashboard" replace />
        } />
      </Routes>
      </Layout>
      <ToastContainer />
    </>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <NotificationProvider>
            <AuthProvider>
              <SettingsProvider>
                <SidebarProvider>
                  <Router>
                    <AppRoutes />
                  </Router>
                </SidebarProvider>
              </SettingsProvider>
            </AuthProvider>
          </NotificationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;